'use client';

import React, { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({ ime: '', email: '', telefon: '', poruka: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ ime: '', email: '', telefon: '', poruka: '' });
  };

  return (
    <section id="kontakt" className="section-padding px-6 bg-secondary/30">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <div className="fade-up-init">
            <span className="text-xs font-bold uppercase tracking-widest text-primary mb-4 block">// Kontaktirajte nas</span>
            <h2 className="text-section-title text-foreground mb-6">
              Kako možemo <br />
              <span className="text-primary">pomoći?</span>
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-10 max-w-md">
              Slobodno nas kontaktirajte za sve informacije o servisu, kupovini vozila ili iznajmljivanju prikolica. Odgovaramo brzo.
            </p>

            <div className="flex flex-col gap-6 mb-10">
              {[
                { icon: 'phone', label: 'Telefon', value: '+387 65 307 500', href: 'tel:+38765307500' },
                { icon: 'phone', label: 'Mobilni', value: '+387 66 983 950', href: 'tel:+38766983950' },
                { icon: 'mail', label: 'Email', value: 'info@bumbarauto.ba', href: 'mailto:info@bumbarauto.ba' },
                { icon: 'pin', label: 'Adresa', value: 'Blagoja Parovica 2, Banja Luka', href: '#' },
              ].map((item) => (
                <a key={item.label} href={item.href} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-sm flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300">
                    {item.icon === 'phone' && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary group-hover:text-primary-foreground transition-colors">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    )}
                    {item.icon === 'mail' && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary group-hover:text-primary-foreground transition-colors">
                        <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                      </svg>
                    )}
                    {item.icon === 'pin' && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary group-hover:text-primary-foreground transition-colors">
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-0.5">{item.label}</span>
                    <span className="text-foreground font-medium group-hover:text-primary transition-colors">{item.value}</span>
                  </div>
                </a>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-sm overflow-hidden border border-border" style={{ height: '220px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2836.123456789!2d17.1850!3d44.7722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475e030b7e3b4e4b%3A0xabcdef1234567890!2sBlagoja%20Parovica%202%2C%20Banja%20Luka!5e0!3m2!1sen!2sba!4v1690000000000!5m2!1sen!2sba"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(0.85)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokacija Auto servisa Bumbar"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-card border border-border rounded-sm p-8 fade-up-init stagger-2">
            <h3 className="text-xl font-bold text-foreground mb-2">Pošaljite poruku</h3>
            <p className="text-muted-foreground text-sm mb-8">Odgovorićemo u roku od 24 sata.</p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-16 h-16 bg-primary/10 border border-primary/30 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                </div>
                <p className="text-foreground font-bold text-lg">Poruka poslana!</p>
                <p className="text-muted-foreground text-sm text-center">Kontaktirat ćemo vas uskoro.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="ime" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ime i prezime</label>
                    <input
                      id="ime"
                      name="ime"
                      type="text"
                      required
                      value={formData.ime}
                      onChange={handleChange}
                      placeholder="Marko Marković"
                      className="bg-input border border-border text-foreground px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="telefon" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Telefon</label>
                    <input
                      id="telefon"
                      name="telefon"
                      type="tel"
                      value={formData.telefon}
                      onChange={handleChange}
                      placeholder="+387 65 XXX XXX"
                      className="bg-input border border-border text-foreground px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email adresa</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="vas@email.ba"
                    className="bg-input border border-border text-foreground px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="poruka" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Poruka</label>
                  <textarea
                    id="poruka"
                    name="poruka"
                    required
                    rows={5}
                    value={formData.poruka}
                    onChange={handleChange}
                    placeholder="Opišite šta vam je potrebno..."
                    className="bg-input border border-border text-foreground px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/50"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-primary text-primary-foreground px-8 py-4 rounded-sm font-bold text-sm tracking-wide uppercase hover:bg-accent transition-colors duration-300 flex items-center justify-center gap-2 mt-2"
                >
                  Pošalji poruku
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14m-7-7 7 7-7 7"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}